/**
 * File row, the file list, and the marks that stand in for a file's contents.
 * Screen spec 4.10 (the file tile) and S13.
 *
 * DEVIATION FROM SPEC S13 ZONE 5, RECORDED, because it is the one structural
 * call on this screen. The spec draws a five column grid of tiles. This is a
 * ledger of rows, for the three reasons the deploys timeline is also rows:
 *
 *   a) The facts a person compares between files are the size and the date,
 *      and comparing numbers down a column is one movement while comparing
 *      them across a grid is one movement per tile.
 *   b) A tile has to truncate a name to about twenty characters. A row gives
 *      it the width of the page, and the name is the whole identity of a file.
 *   c) A grid of image tiles would make the largest, most colourful objects on
 *      an Instrument screen the thumbnails, which are decoration, and they
 *      would out shout the lamps, which are the only things allowed to pull
 *      the eye in this product.
 *
 * The thumbnail survives the change: it sits at the head of its row at 20px,
 * and the full size preview is one click away in the file sheet, which is
 * where the copy field and the delete action can be full width instead of
 * squeezed into a table cell.
 *
 * THE MARK FOR AN IMAGE IS DETERMINISTIC AND IT IS NOT A PHOTOGRAPH. The
 * prototype has no bytes to render, and a stock photograph dropped in here
 * would be the one thing on the screen that is not true. So an image draws the
 * same 4x4 two tone fingerprint the project avatars use, derived from the file
 * name, which is honest, reproducible in a screenshot, and carries no colour.
 *
 * Every row is min-height, never height: a Thai name at 1.4x wraps and the row
 * grows, which is the only acceptable direction for that to go.
 */

import type { ReactNode } from 'react';
import { Lamp } from './StatusPill';
import { bytes as formatBytes, middleTruncate } from '../../lib/format';
import { t } from '../../lib/i18n/t';
import type { UploadStatus } from '../../lib/types';
import '../../styles/components/file-row.css';

/** How long a name gets before it truncates from the middle. */
export const NAME_BUDGET = 52;

export type FileKind = 'image' | 'vector' | 'document' | 'data' | 'file';

/**
 * The kind is read from the content type first and the ending second, because
 * a file storage that lost a content type still has a name, and a person who
 * uploaded eleven photographs should not see eleven blank sheets of paper.
 */
export function fileKind(contentType: string, name = ''): FileKind {
  const dot = name.lastIndexOf('.');
  const ext = dot === -1 ? '' : name.slice(dot + 1).toLowerCase();

  if (contentType === 'image/svg+xml' || ext === 'svg') return 'vector';
  if (contentType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'tiff'].includes(ext)) {
    return 'image';
  }
  if (contentType === 'application/pdf' || ext === 'pdf') return 'document';
  if (['text/csv', 'application/json', 'text/plain'].includes(contentType) || ['csv', 'json', 'txt'].includes(ext)) {
    return 'data';
  }
  return 'file';
}

/** The word beside the kind, for the file sheet. No acronym, per copy rule 5. */
export function kindWord(kind: FileKind): string {
  if (kind === 'image') return t('Image');
  if (kind === 'vector') return t('Drawing');
  if (kind === 'document') return t('Document');
  if (kind === 'data') return t('Data');
  return t('File');
}

/**
 * A stable 32 bit hash of a name. Sixteen cell pairs, one per grid cell, and
 * the file sheet borrows it for the token in a link that expires, so one file
 * draws one mark and one token for as long as it exists.
 */
export function fileHash(value: string): number {
  let hash = 2_166_136_261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

const LINE_ART: Record<Exclude<FileKind, 'image'>, ReactNode> = {
  vector: (
    <>
      <path d="M12 4.5 20 19.5H4Z" />
      <circle cx="12" cy="4.5" r="1.6" />
      <circle cx="20" cy="19.5" r="1.6" />
      <circle cx="4" cy="19.5" r="1.6" />
    </>
  ),
  document: (
    <>
      <path d="M6 3h8l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v5h5M8.5 13h7M8.5 17h4" />
    </>
  ),
  data: (
    <>
      <rect x="3.5" y="5" width="17" height="14" rx="1.5" />
      <path d="M3.5 10h17M10 10v9" />
    </>
  ),
  file: (
    <>
      <path d="M6 3h8l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v5h5" />
    </>
  ),
};

export type FileMarkSize = 'sm' | 'lg';

export interface FileGlyphProps {
  kind: FileKind;
  /** Seeds the fingerprint. Two files with one name draw one mark, on purpose. */
  name: string;
  size?: FileMarkSize;
}

/** The thumbnail slot: a fingerprint for an image, line art for everything else. */
export function FileGlyph({ kind, name, size = 'sm' }: FileGlyphProps) {
  const px = size === 'lg' ? 96 : 20;

  if (kind === 'image') {
    const hash = fileHash(name);
    const cells = Array.from({ length: 16 }, (_, i) => (hash >> (i * 2)) & 3);

    return (
      <span className="fmark" data-size={size} aria-hidden="true">
        <svg viewBox="0 0 16 16" width={px} height={px} focusable="false">
          {cells.map((tone, i) =>
            tone === 0 ? null : (
              <rect
                key={i}
                x={(i % 4) * 4 + 0.35}
                y={Math.floor(i / 4) * 4 + 0.35}
                width={3.3}
                height={3.3}
                rx={0.6}
                fill="currentColor"
                fillOpacity={tone === 1 ? 0.32 : 0.72}
              />
            ),
          )}
        </svg>
      </span>
    );
  }

  return (
    <span className="fmark" data-size={size} data-line="yes" aria-hidden="true">
      <svg
        viewBox="0 0 24 24"
        width={size === 'lg' ? 72 : 18}
        height={size === 'lg' ? 72 : 18}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
        focusable="false"
      >
        {LINE_ART[kind]}
      </svg>
    </span>
  );
}

/**
 * What a row shows while a file is still arriving. The track never runs
 * without the file's name beside it, which is screen spec 4.9's single
 * narrated line expressed as a row: the name is the sentence.
 */
export interface UploadView {
  status: UploadStatus;
  /** 0 to 1, read from the clock, never from a counter. */
  fraction: number;
  /** One line, under the name, when a transfer did not land. */
  reason?: string;
}

export interface FileRowProps {
  /** The name as it should read here: a base name, or a path under the folder. */
  name: string;
  bytes: number;
  kind: FileKind;
  /** Already formatted, already translated. The date rule lives at the call site. */
  when?: string;
  /** The design/assets footgun chip. Its explainer is the caller's title text. */
  fromYourCode?: boolean;
  fromYourCodeHint?: string;
  /** Opens the file. A row with no handler is not a control. */
  onOpen?: () => void;
  /** Present only while the file is on its way up, or once it stopped. */
  upload?: UploadView;
  /** One trailing control. Sits outside the row's own target. */
  action?: ReactNode;
}

export function FileList({ label, children }: { label: string; children: ReactNode }) {
  return (
    <ul className="flist" aria-label={label}>
      {children}
    </ul>
  );
}

export function FileRow({
  name,
  bytes,
  kind,
  when,
  fromYourCode = false,
  fromYourCodeHint,
  onOpen,
  upload,
  action,
}: FileRowProps) {
  const uploading = upload?.status === 'uploading';
  const stopped = upload?.status === 'didnt-upload';
  const percent = Math.round((upload?.fraction ?? 0) * 100);

  return (
    <li className="flist__item">
      <div className="frow" data-open={onOpen ? 'yes' : 'no'} data-stopped={stopped ? 'yes' : 'no'}>
        {/* The stretched target. It sits under everything else in the row so
            the trailing control stays clickable, and it carries the whole
            row's accessible name so a keyboard lands on one thing, not five. */}
        {onOpen ? (
          <button
            type="button"
            className="frow__open"
            onClick={onOpen}
            aria-label={t('Open {name}', { name })}
          />
        ) : null}

        <span className="frow__glyph">
          <FileGlyph kind={kind} name={name} />
        </span>

        <span className="frow__main">
          <span className="frow__name mono" title={name}>
            {middleTruncate(name, NAME_BUDGET)}
          </span>
          {fromYourCode ? (
            <span className="frow__chip" title={fromYourCodeHint}>
              {t('From your code')}
            </span>
          ) : null}
          {upload?.reason ? <span className="frow__reason">{upload.reason}</span> : null}
        </span>

        <span className="frow__size mono">{formatBytes(bytes)}</span>

        <span className="frow__when">
          {uploading ? (
            <span className="frow__state" data-tone="building">
              <Lamp value="deploying" size="sm" />
              {t('Uploading')}
              <span className="frow__pct mono">{t('{n}%', { n: percent })}</span>
            </span>
          ) : null}

          {stopped ? (
            <span className="frow__state" data-tone="attention">
              <span className="frow__cross" aria-hidden="true">
                <svg viewBox="0 0 16 16" width="10" height="10" focusable="false">
                  <path
                    d="M4 4l8 8M12 4l-8 8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              {t("Didn't upload")}
            </span>
          ) : null}

          {!upload ? <span className="frow__when-text">{when ?? ''}</span> : null}
        </span>

        {action ? <span className="frow__action">{action}</span> : null}

        {/* A width, not an animation: the clock moves it, so the only keyframed
            motion in the row is the neutral arc in the lamp. */}
        {uploading ? (
          <span className="frow__track" aria-hidden="true">
            <span className="frow__bar" style={{ inlineSize: `${percent}%` }} />
          </span>
        ) : null}
      </div>
    </li>
  );
}
